import { slugify } from '../utils/utils';

/**
 * Département d'une page ville (FIX-938).
 *
 * `/annuaire-velo/:dept/:city` ne vérifiait que la slugification : la page
 * ville est construite à partir du seul slug de ville, si bien que
 * `/annuaire-velo/n-importe-quoi/geneve` servait en 200 la page de Genève,
 * canonical sur elle-même. L'api expose pour chaque ville (`GET
 * /shops/cities/{ville}`) :
 *
 *   dept_slug   le département canonique — celui qui porte le plus de fiches
 *   dept_slugs  tous les départements sous lesquels la ville a des fiches
 *               (premier segment de l'url_path de chacune)
 *
 * Une ville homonyme (Mons : belgique et herault) reste servie sous chacun de
 * ses vrais départements, sans 301 entre eux — un 301 est mis en cache par
 * Google, et les fusionner a été refusé (Clément, 10/10). Seul un département
 * qui n'est PAS celui de la ville redirige, vers `dept_slug`.
 */

/**
 * Le slug de ville tel que la page le demande à l'api : un suffixe code postal
 * (`paris-75001`) est retiré. La route serveur et `AnnuaireCity` doivent
 * interroger la même ville, sinon la 301 se décide sur une autre réponse que
 * celle que la page affiche.
 */
export const parseCitySlug = (slug) => {
  const splitSlug = slug.split('-');
  if (parseInt(splitSlug[splitSlug.length - 1], 10)) {
    return slug.slice(0, slug.length - 6);
  }
  return slug;
};

/**
 * Le slug de ville à chercher pour la route `/annuaire-velo/:dept/:city`, ou
 * `null` quand ce n'est pas une page ville (`/annuaire-velo/letter/:lettre`).
 */
export function cityLookupSlug(dept, city) {
  if (slugify(dept) === 'letter') return null;
  return parseCitySlug(slugify(city));
}

/**
 * Réponse de `GET /shops/cities/{ville}` → `{ dept_slug, dept_slugs }`, ou
 * `null` quand elle ne permet pas de conclure (ville inconnue, api antérieure
 * à `dept_slugs`).
 */
export function cityDeptsFromResponse(json) {
  if (!json || !json.dept_slug || !Array.isArray(json.dept_slugs)) {
    return null;
  }
  return { dept_slug: json.dept_slug, dept_slugs: json.dept_slugs };
}

/**
 * Le département vers lequel rediriger, ou `null` pour servir la page telle
 * quelle.
 *
 * `null` aussi quand l'api ne dit rien d'exploitable (ville inconnue, réponse
 * en échec, api antérieure à `dept_slugs`) : jamais de 301 inventée. Sans
 * `dept_slugs`, `dept_slug` seul ne suffit pas à conclure : pour une ville
 * homonyme il enverrait un vrai département vers l'autre.
 */
export function cityPageRedirectDept(requestedDept, cityData) {
  if (!cityData || typeof cityData !== 'object') return null;

  const { dept_slug: canonical, dept_slugs: depts } = cityData;
  if (!canonical || !Array.isArray(depts) || !depts.includes(canonical)) {
    return null;
  }

  return depts.includes(requestedDept) ? null : canonical;
}

/**
 * La cible de la 301 pour `/annuaire-velo/:dept/:city`, ou `null` pour rendre
 * la page. Slugification et département se corrigent dans le même saut
 * (`/AVEYRON/geneve` → `/suisse/geneve`), jamais en deux.
 */
export function cityPageRedirectPath(dept, city, cityData) {
  const slugifiedDept = slugify(dept);
  const slugifiedCity = slugify(city);
  const targetDept =
    cityPageRedirectDept(slugifiedDept, cityData) || slugifiedDept;
  if (dept === targetDept && city === slugifiedCity) return null;
  return `/annuaire-velo/${targetDept}/${slugifiedCity}`;
}
